import React, { useEffect, useState } from "react";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import RecipeCategoryModal from "./Components/RecipeCategoryModal";
import RecipeModal from "./Components/RecipeModal";
import api from "../../api";
import { IMAGE_URL } from "../../path";
import {
    RecipeCategory,
    RecipeProduct,
    Recipe,
} from "../../Recipe";
import "../../styles/Login.scss";

const AdminRecipes: React.FC = () => {
    const [categories, setCategories] = useState<RecipeCategory[]>([]);
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [products, setProducts] = useState<RecipeProduct[]>([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [selectedCategory, setSelectedCategory] =
        useState<RecipeCategory | null>(null);

    const [selectedRecipe, setSelectedRecipe] =
        useState<Recipe | null>(null);

    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showRecipeModal, setShowRecipeModal] = useState(false);

    const [deletingCategoryId, setDeletingCategoryId] =
        useState<number | null>(null);

    const [deletingRecipeId, setDeletingRecipeId] =
        useState<number | null>(null);

    const [movingCategoryId, setMovingCategoryId] =
        useState<number | null>(null);

    const [movingRecipeId, setMovingRecipeId] =
        useState<number | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");

            const [
                categoriesResponse,
                recipesResponse,
                productsResponse,
            ] = await Promise.all([
                api.get("/admin/recipe-categories"),
                api.get("/admin/recipes"),
                api.get("/admin/products"),
            ]);

            setCategories(
                categoriesResponse.data.data ?? categoriesResponse.data
            );

            setRecipes(
                recipesResponse.data.data ?? recipesResponse.data
            );

            setProducts(
                productsResponse.data.data ?? productsResponse.data
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to load recipes."
            );
        } finally {
            setLoading(false);
        }
    };

    /*
     * =========================================================
     * CATEGORY MODAL
     * =========================================================
     */

    const handleAddCategory = () => {
        setSelectedCategory(null);
        setShowCategoryModal(true);
        setError("");
        setSuccess("");
    };

    const handleEditCategory = (category: RecipeCategory) => {
        setSelectedCategory(category);
        setShowCategoryModal(true);
        setError("");
        setSuccess("");
    };

    const handleCloseCategoryModal = () => {
        setShowCategoryModal(false);
        setSelectedCategory(null);
    };

    const handleCategorySaved = async () => {
        handleCloseCategoryModal();

        setSuccess("Recipe category saved successfully.");

        await loadData();
    };

    /*
     * =========================================================
     * RECIPE MODAL
     * =========================================================
     */

    const handleAddRecipe = () => {
        setSelectedRecipe(null);
        setShowRecipeModal(true);
        setError("");
        setSuccess("");
    };

    const handleEditRecipe = (recipe: Recipe) => {
        setSelectedRecipe(recipe);
        setShowRecipeModal(true);
        setError("");
        setSuccess("");
    };

    const handleCloseRecipeModal = () => {
        setShowRecipeModal(false);
        setSelectedRecipe(null);
    };

    const handleRecipeSaved = async () => {
        handleCloseRecipeModal();

        setSuccess("Recipe saved successfully.");

        await loadData();
    };

    /*
     * =========================================================
     * CATEGORY DELETE
     * =========================================================
     */

    const handleDeleteCategory = async (
        category: RecipeCategory
    ) => {
        if (category.recipes_count > 0) {
            setError(
                "This recipe category cannot be deleted because it still contains recipes."
            );
            setSuccess("");
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete "${category.name}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingCategoryId(category.id);
            setError("");
            setSuccess("");

            await api.delete(
                `/admin/recipe-categories/${category.id}`
            );

            setSuccess(
                "Recipe category deleted successfully."
            );

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to delete recipe category."
            );
        } finally {
            setDeletingCategoryId(null);
        }
    };

    /*
     * =========================================================
     * RECIPE DELETE
     * =========================================================
     */

    const handleDeleteRecipe = async (recipe: Recipe) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${recipe.name}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingRecipeId(recipe.id);
            setError("");
            setSuccess("");

            await api.delete(`/admin/recipes/${recipe.id}`);

            setSuccess("Recipe deleted successfully.");

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to delete recipe."
            );
        } finally {
            setDeletingRecipeId(null);
        }
    };

    /*
     * =========================================================
     * MOVE CATEGORY
     * =========================================================
     */

    const handleMoveCategory = async (
        category: RecipeCategory,
        direction: "up" | "down"
    ) => {
        try {
            setMovingCategoryId(category.id);
            setError("");
            setSuccess("");

            await api.post(
                `/admin/recipe-categories/${category.id}/move`,
                {
                    direction,
                }
            );

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to move recipe category."
            );
        } finally {
            setMovingCategoryId(null);
        }
    };

    /*
     * =========================================================
     * MOVE RECIPE
     * =========================================================
     */

    const handleMoveRecipe = async (
        recipe: Recipe,
        direction: "up" | "down"
    ) => {
        try {
            setMovingRecipeId(recipe.id);
            setError("");
            setSuccess("");

            await api.post(
                `/admin/recipes/${recipe.id}/move`,
                {
                    direction,
                }
            );

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to move recipe."
            );
        } finally {
            setMovingRecipeId(null);
        }
    };

    /*
     * =========================================================
     * CATEGORY RECIPES
     * =========================================================
     */

    const getRecipesForCategory = (categoryId: number) => {
        return recipes.filter(
            (recipe) =>
                recipe.recipe_category_id === categoryId
        );
    };

    /*
     * =========================================================
     * CATEGORY MOVE STATE
     * =========================================================
     */

    const canMoveCategoryUp = (index: number) => {
        return index > 0;
    };

    const canMoveCategoryDown = (index: number) => {
        return index < categories.length - 1;
    };

    /*
     * =========================================================
     * RECIPE MOVE STATE
     * =========================================================
     */

    const canMoveRecipeUp = (
        recipe: Recipe,
        categoryRecipes: Recipe[]
    ) => {
        const index = categoryRecipes.findIndex(
            (item) => item.id === recipe.id
        );

        return index > 0;
    };

    const canMoveRecipeDown = (
        recipe: Recipe,
        categoryRecipes: Recipe[]
    ) => {
        const index = categoryRecipes.findIndex(
            (item) => item.id === recipe.id
        );

        return index < categoryRecipes.length - 1;
    };

    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (
        <div className="admin-page">
            <AdminHeader />

            <div className="admin-body">
                <AdminSidebar />

                <main className="admin-content">
                    <div className="admin-users">

                        {/* =================================================
                            PAGE HEADER
                        ================================================== */}

                        <div className="admin-page-header">
                            <div>
                                <h1>Recipes</h1>
                                <p>
                                    Manage recipe categories and recipes.
                                </p>
                            </div>

                            <div className="admin-user-actions">
                                <button
                                    type="button"
                                    className="admin-primary-button"
                                    onClick={handleAddCategory}
                                >
                                    Add Category
                                </button>

                                <button
                                    type="button"
                                    className="admin-primary-button"
                                    onClick={handleAddRecipe}
                                >
                                    Add Recipe
                                </button>
                            </div>
                        </div>

                        {/* =================================================
                            MESSAGES
                        ================================================== */}

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        {loading ? (
                            <p>Loading recipes...</p>
                        ) : (
                            <>
                                {/* =============================================
                                    RECIPE CATEGORIES
                                ============================================== */}

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>Recipe Categories</h2>
                                            <p>
                                                Manage the categories used to
                                                organise recipes.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={handleAddCategory}
                                        >
                                            Add Category
                                        </button>
                                    </div>

                                    {categories.length === 0 ? (
                                        <p>
                                            No recipe categories found.
                                        </p>
                                    ) : (
                                        <div className="admin-users-table">
                                            <table>
                                                <thead>
                                                <tr>
                                                    <th>Icon</th>
                                                    <th>Category</th>
                                                    <th>Recipes</th>
                                                    <th>Active</th>
                                                    <th>Actions</th>
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {categories.map(
                                                    (
                                                        category,
                                                        index
                                                    ) => (
                                                        <tr
                                                            key={
                                                                category.id
                                                            }
                                                        >
                                                            <td>
                                                                {category.icon ? (
                                                                    <img
                                                                        src={`${IMAGE_URL}/${category.icon}`}
                                                                        alt={`${category.name} icon`}
                                                                        className="admin-svg-preview"
                                                                    />
                                                                ) : (
                                                                    <span>—</span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <strong>
                                                                    {
                                                                        category.name
                                                                    }
                                                                </strong>
                                                            </td>

                                                            <td>
                                                                {
                                                                    category.recipes_count
                                                                }
                                                            </td>

                                                            <td>
                                                                    <span
                                                                        className={`admin-role ${
                                                                            category.active
                                                                                ? "admin-role-admin"
                                                                                : "admin-role-editor"
                                                                        }`}
                                                                    >
                                                                        {category.active
                                                                            ? "Active"
                                                                            : "Inactive"}
                                                                    </span>
                                                            </td>

                                                            <td>
                                                                <div className="admin-user-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        disabled={
                                                                            movingCategoryId ===
                                                                            category.id ||
                                                                            !canMoveCategoryUp(
                                                                                index
                                                                            )
                                                                        }
                                                                        onClick={() =>
                                                                            handleMoveCategory(
                                                                                category,
                                                                                "up"
                                                                            )
                                                                        }
                                                                    >
                                                                        ↑
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        disabled={
                                                                            movingCategoryId ===
                                                                            category.id ||
                                                                            !canMoveCategoryDown(
                                                                                index
                                                                            )
                                                                        }
                                                                        onClick={() =>
                                                                            handleMoveCategory(
                                                                                category,
                                                                                "down"
                                                                            )
                                                                        }
                                                                    >
                                                                        ↓
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        onClick={() =>
                                                                            handleEditCategory(
                                                                                category
                                                                            )
                                                                        }
                                                                    >
                                                                        Edit
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button admin-table-button-danger"
                                                                        disabled={
                                                                            deletingCategoryId ===
                                                                            category.id
                                                                        }
                                                                        onClick={() =>
                                                                            handleDeleteCategory(
                                                                                category
                                                                            )
                                                                        }
                                                                    >
                                                                        {deletingCategoryId ===
                                                                        category.id
                                                                            ? "Deleting..."
                                                                            : "Delete"}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>

                                {/* =============================================
                                    RECIPES
                                ============================================== */}

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>Recipes</h2>
                                            <p>
                                                Manage recipes within each
                                                category.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={handleAddRecipe}
                                        >
                                            Add Recipe
                                        </button>
                                    </div>

                                    {categories.length === 0 ? (
                                        <p>
                                            Create a recipe category before
                                            adding recipes.
                                        </p>
                                    ) : (
                                        categories.map((category) => {
                                            const categoryRecipes =
                                                getRecipesForCategory(
                                                    category.id
                                                );

                                            return (
                                                <div
                                                    key={category.id}
                                                    className="admin-content-section"
                                                >
                                                    <div className="admin-page-header">
                                                        <div>
                                                            <h3>
                                                                {
                                                                    category.name
                                                                }
                                                            </h3>

                                                            <p>
                                                                {
                                                                    categoryRecipes.length
                                                                }{" "}
                                                                recipe
                                                                {categoryRecipes.length !==
                                                                1
                                                                    ? "s"
                                                                    : ""}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {categoryRecipes.length ===
                                                    0 ? (
                                                        <p>
                                                            No recipes in this
                                                            category.
                                                        </p>
                                                    ) : (
                                                        <div className="admin-users-table">
                                                            <table>
                                                                <thead>
                                                                <tr>
                                                                    <th>
                                                                        Image
                                                                    </th>
                                                                    <th>
                                                                        Recipe
                                                                    </th>
                                                                    <th>
                                                                        Product
                                                                    </th>
                                                                    <th>
                                                                        Active
                                                                    </th>
                                                                    <th>
                                                                        Actions
                                                                    </th>
                                                                </tr>
                                                                </thead>

                                                                <tbody>
                                                                {categoryRecipes.map(
                                                                    (
                                                                        recipe
                                                                    ) => (
                                                                        <tr
                                                                            key={
                                                                                recipe.id
                                                                            }
                                                                        >
                                                                            <td>
                                                                                {recipe.preview_image ? (
                                                                                    <img
                                                                                        src={`${IMAGE_URL}/${recipe.preview_image}`}
                                                                                        alt={
                                                                                            recipe.name
                                                                                        }
                                                                                        style={{
                                                                                            width: "80px",
                                                                                            height: "60px",
                                                                                            objectFit:
                                                                                                "cover",
                                                                                            borderRadius:
                                                                                                "6px",
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <span>
                                                                                            —
                                                                                        </span>
                                                                                )}
                                                                            </td>

                                                                            <td>
                                                                                {
                                                                                    recipe.name
                                                                                }
                                                                            </td>

                                                                            <td>
                                                                                {recipe.product
                                                                                    ? recipe
                                                                                        .product
                                                                                        .name
                                                                                    : "—"}
                                                                            </td>

                                                                            <td>
                                                                                    <span
                                                                                        className={`admin-role ${
                                                                                            recipe.active
                                                                                                ? "admin-role-admin"
                                                                                                : "admin-role-editor"
                                                                                        }`}
                                                                                    >
                                                                                        {recipe.active
                                                                                            ? "Active"
                                                                                            : "Inactive"}
                                                                                    </span>
                                                                            </td>

                                                                            <td>
                                                                                <div className="admin-user-actions">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="admin-table-button"
                                                                                        disabled={
                                                                                            movingRecipeId ===
                                                                                            recipe.id ||
                                                                                            !canMoveRecipeUp(
                                                                                                recipe,
                                                                                                categoryRecipes
                                                                                            )
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleMoveRecipe(
                                                                                                recipe,
                                                                                                "up"
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        ↑
                                                                                    </button>

                                                                                    <button
                                                                                        type="button"
                                                                                        className="admin-table-button"
                                                                                        disabled={
                                                                                            movingRecipeId ===
                                                                                            recipe.id ||
                                                                                            !canMoveRecipeDown(
                                                                                                recipe,
                                                                                                categoryRecipes
                                                                                            )
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleMoveRecipe(
                                                                                                recipe,
                                                                                                "down"
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        ↓
                                                                                    </button>

                                                                                    <button
                                                                                        type="button"
                                                                                        className="admin-table-button"
                                                                                        onClick={() =>
                                                                                            handleEditRecipe(
                                                                                                recipe
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </button>

                                                                                    <button
                                                                                        type="button"
                                                                                        className="admin-table-button admin-table-button-danger"
                                                                                        disabled={
                                                                                            deletingRecipeId ===
                                                                                            recipe.id
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleDeleteRecipe(
                                                                                                recipe
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        {deletingRecipeId ===
                                                                                        recipe.id
                                                                                            ? "Deleting..."
                                                                                            : "Delete"}
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </section>
                            </>
                        )}
                    </div>
                </main>
            </div>

            {/* =========================================================
                CATEGORY MODAL
            ========================================================= */}

            {showCategoryModal && (
                <RecipeCategoryModal
                    category={selectedCategory}
                    onClose={handleCloseCategoryModal}
                    onSaved={handleCategorySaved}
                />
            )}

            {/* =========================================================
                RECIPE MODAL
            ========================================================= */}

            {showRecipeModal && (
                <RecipeModal
                    recipe={selectedRecipe}
                    categories={categories}
                    products={products}
                    onClose={handleCloseRecipeModal}
                    onSaved={handleRecipeSaved}
                />
            )}
        </div>
    );
};

export default AdminRecipes;
